/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { IEditor } from "../IEditor";
import { IEditorApiService } from "../api/IEditorApiService";
import { BrowserLogger } from "../common/BrowserLogger";
import { createEventEmitter, emitEvent, removeAllEventListeners } from "@adapptio/lib-shared";
import { IntegrationParamsResolver } from "./Resolvers/IntegrationParamsResolver";
import { IntegrationRefResolver } from "./Resolvers/IntegrationRefResolver";
import { ResourceService } from "../resources/ResourceService";
import { IIntegrationFunction, IIntegrationType } from "./IntegrationServiceTypes";
import { BP, importSchema, ISchemaConstObject, TSchemaConstObjectProps } from "@adapptio/lib-blueprint";
import { ActionRefResolver } from "../runtime/Resolvers/ActionRefResolver";

export interface IIntegrationServiceBaseResolvers {
	integrationParams: IntegrationParamsResolver;
	integrationRef: IntegrationRefResolver;
	actionRef: ActionRefResolver;
}

export class IntegrationService {
	/** Logger facility */
	private logger: BrowserLogger;

	/* Dependency services */
	private resourceSvc: ResourceService;
	private apiService: IEditorApiService;

	/** Integration types */
	private integrationTypes: IIntegrationType[] = [];

	/** Index of integration type name -> type object */
	private typeIndexByName: { [K: string]: IIntegrationType } = {};

	/** Index of integration type name -> list of integration functions */
	private fuctionIndexByType: { [K: string]: IIntegrationFunction[] } = {};

	/** Index of "<integration type name>:<function name>" -> integration function object */
	private functionIndexByTypeName: { [K: string]: IIntegrationFunction } = {};

	/** Emitted when integration types have changed or reloaded */
	public onIntegrationTypesChange = createEventEmitter<void>();

	/** Cached base resolver */
	private baseResolvers: IIntegrationServiceBaseResolvers;

	/**
	 * Service constructor
	 *
	 * @param dc Dependency Container
	 */
	constructor(dc: IEditor) {
		this.logger = dc.get("logger").facility("IntegrationService");

		this.resourceSvc = dc.get("resourceService");
		this.apiService = dc.get("apiService");

		this.createBaseResolvers();
	}

	/**
	 * Initializes the service
	 */
	public async init(): Promise<void> {
		await this.loadIntegrationTypes();
	}

	/**
	 * Disposes the service
	 */
	public dispose(): void {
		removeAllEventListeners(this.onIntegrationTypesChange);
	}

	/**
	 * Loads integration types from the API
	 */
	private async loadIntegrationTypes(): Promise<void> {
		try {
			const apiRes = await this.apiService.getIntegrationTypes();

			this.integrationTypes = [];
			this.typeIndexByName = {};
			this.fuctionIndexByType = {};
			this.functionIndexByTypeName = {};

			apiRes.forEach((item) => {
				try {
					// Create integration type object
					const typeObject: IIntegrationType = {
						integrationType: item.integrationType,
						label: item.label,
						category: item.category,
						description: item.description,
						icon: item.icon,
						docUrl: item.docUrl,
						order: item.order,
						hidden: !!item.hidden,
						optionsSchema: BP.Const.Map({
							value: importSchema<ISchemaConstObject<TSchemaConstObjectProps>>(
								item.optionsSchema
							)
						}),
						functions: []
					};

					// Add to indexes
					this.integrationTypes.push(typeObject);
					this.typeIndexByName[typeObject.integrationType] = typeObject;
					this.fuctionIndexByType[typeObject.integrationType] = [];

					// Load functions
					item.functions.forEach((fn) => {
						try {
							// Create function object
							const functionObject: IIntegrationFunction = {
								functionName: fn.functionName,
								label: fn.label,
								description: fn.description,
								icon: fn.icon,
								docUrl: fn.docUrl,
								order: fn.order,
								paramsSchema: importSchema<ISchemaConstObject<TSchemaConstObjectProps>>(
									fn.paramsSchema
								)
							};

							// Add to indexes
							typeObject.functions.push(functionObject);
							this.fuctionIndexByType[typeObject.integrationType].push(functionObject);
							this.functionIndexByTypeName[
								typeObject.integrationType + ":" + functionObject.functionName
							] = functionObject;
						} catch (err) {
							// eslint-disable-next-line max-len
							this.logger.error(
								`Failed to import integration function "${item.integrationType}:${fn.functionName}":`,
								err
							);
						}
					});
				} catch (err) {
					this.logger.error(`Failed to import integration type "${item.integrationType}":`, err);
				}
			});

			this.logger.debug("Integration types loaded:", this.integrationTypes);
			emitEvent(this.onIntegrationTypesChange);
		} catch (err) {
			this.logger.error("Failed to load integration types:", err);
		}
	}

	/**
	 * Creates and caches base resolvers
	 */
	private createBaseResolvers(): void {
		this.baseResolvers = {
			integrationParams: new IntegrationParamsResolver(this),
			integrationRef: new IntegrationRefResolver(this.resourceSvc, this),
			actionRef: new ActionRefResolver(this.resourceSvc)
		};
	}

	/**
	 * Returns base resolvers
	 */
	public getBaseResolvers(): IIntegrationServiceBaseResolvers {
		return this.baseResolvers;
	}

	/**
	 * Returns all integration types
	 */
	public getIntegrationTypes(): IIntegrationType[] {
		return this.integrationTypes;
	}

	/**
	 * Returns a single integration type
	 *
	 * @param integrationType Integration type name
	 */
	public getIntegrationType(integrationType: string): IIntegrationType {
		return this.typeIndexByName[integrationType] || null;
	}

	/**
	 * Returns whenever integration type exists
	 *
	 * @param integrationType Integration type name
	 */
	public integrationTypeExists(integrationType: string): boolean {
		return !!this.typeIndexByName[integrationType];
	}

	/**
	 * Returns functions for integration type
	 *
	 * @param integrationType Integration type name
	 */
	public getIntegrationFunctions(integrationType: string): IIntegrationFunction[] {
		return this.fuctionIndexByType[integrationType] || [];
	}

	/**
	 * Returns single integration function for a given type
	 *
	 * @param integrationType Integration type name
	 * @param functionName Function name
	 */
	public getSingleIntegrationFunction(integrationType: string, functionName: string): IIntegrationFunction {
		return this.functionIndexByTypeName[integrationType + ":" + functionName] || null;
	}
}
